import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Card from '../ui/Card';

export default function AdminContentEditor({ content, onContentChange, formules, onFormulesChange }) {
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    projet: false,
    brief: false,
    formules: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateField = (section, field, value) => {
    onContentChange({
      ...content,
      [section]: {
        ...content[section],
        [field]: value
      }
    });
  };

  const updateNestedField = (section, parent, field, value) => {
    onContentChange({
      ...content,
      [section]: {
        ...content[section],
        [parent]: {
          ...content[section][parent],
          [field]: value
        }
      }
    });
  };

  const updateBriefSection = (briefKey, field, value) => {
    onContentChange({
      ...content,
      brief: {
        ...content.brief,
        sections: {
          ...content.brief.sections,
          [briefKey]: {
            ...content.brief.sections[briefKey],
            [field]: value
          }
        }
      }
    });
  };

  const updateFormule = (formuleId, field, value) => {
    onFormulesChange(
      formules.map((f) =>
        f.id === formuleId
          ? { ...f, [field]: value }
          : f
      )
    );
  };

  const updateFormulePricing = (formuleId, field, value) => {
    onFormulesChange(
      formules.map((f) => {
        if (f.id !== formuleId) return f;

        const updatedPricing = {
          ...f.pricing,
          [field]: value
        };

        // Recalcul automatique du prix final si on change jours ou réduction
        if (field === 'days' || field === 'discount') {
          const basePrice = updatedPricing.days * 400;
          const finalPrice = Math.round(basePrice * (1 - updatedPricing.discount / 100));

          return {
            ...f,
            pricing: {
              ...updatedPricing,
              basePrice,
              finalPrice
            }
          };
        }

        return {
          ...f,
          pricing: updatedPricing
        };
      })
    );
  };

  const renderInput = (label, value, onChange, type = 'text', disabled = false) => (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-white mb-2">{label}</label>
      {type === 'textarea' ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-secondary-800 border border-secondary-700 rounded-lg text-white focus:outline-none focus:border-primary-500 min-h-[100px] ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          rows={4}
        />
      ) : (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className={`w-full px-4 py-2 bg-secondary-800 border border-secondary-700 rounded-lg text-white focus:outline-none focus:border-primary-500 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
      )}
    </div>
  );

  const renderSection = (sectionKey, title, children) => (
    <Card key={sectionKey} padding="none" className="mb-6">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-secondary-800 transition"
      >
        <h2 className="text-xl font-bold text-white">{title}</h2>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="text-primary-500" size={24} />
        ) : (
          <ChevronDown className="text-primary-500" size={24} />
        )}
      </button>

      {expandedSections[sectionKey] && (
        <div className="px-6 pb-6 border-t border-secondary-700 pt-6">
          {children}
        </div>
      )}
    </Card>
  );

  return (
    <div className="max-w-5xl mx-auto">
      {/* HERO Section */}
      {renderSection('hero', '🎬 Section Hero', (
        <>
          {renderInput('Badge', content.hero?.badge, (v) => updateField('hero', 'badge', v))}
          {renderInput('Titre', content.hero?.title, (v) => updateField('hero', 'title', v))}
          {renderInput('Titre en Surbrillance', content.hero?.titleHighlight, (v) => updateField('hero', 'titleHighlight', v))}
          {renderInput('Sous-titre', content.hero?.subtitle, (v) => updateField('hero', 'subtitle', v))}
          {renderInput('Description', content.hero?.description, (v) => updateField('hero', 'description', v), 'textarea')}
        </>
      ))}

      {/* PROJET Section */}
      {renderSection('projet', '📁 Section Projet', (
        <>
          {renderInput('Titre', content.projet?.title, (v) => updateField('projet', 'title', v))}
          {renderInput('Introduction', content.projet?.intro, (v) => updateField('projet', 'intro', v), 'textarea')}
        </>
      ))}

      {/* FORMULES Section */}
      {formules && renderSection('formules', '💰 Tarifs & Formules', (
        <>
          <p className="text-secondary-300 mb-6">
            Modifiez les prix et descriptions de vos 3 formules. Le TJM est toujours à 400€.
          </p>

          {formules.map((formule, index) => (
            <div key={formule.id} className="mb-8 p-6 bg-secondary-800 rounded-lg border-2 border-secondary-700">
              <h3 className="text-xl font-bold text-primary-500 mb-4">
                Formule {index + 1} : {formule.name}
              </h3>

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {renderInput('Nom de la formule', formule.name, (v) => updateFormule(formule.id, 'name', v))}
                {renderInput('Tagline', formule.tagline, (v) => updateFormule(formule.id, 'tagline', v))}
              </div>
              {renderInput('Description', formule.description, (v) => updateFormule(formule.id, 'description', v), 'textarea')}

              {/* Pricing */}
              <div className="mt-6 p-4 bg-black/30 rounded-lg border border-primary-700">
                <h4 className="text-white font-semibold mb-3">💵 Tarification</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {renderInput('Nombre de jours', formule.pricing.days, (v) => updateFormulePricing(formule.id, 'days', parseFloat(v) || 0), 'number')}
                  {renderInput('Réduction (%)', formule.pricing.discount, (v) => updateFormulePricing(formule.id, 'discount', parseInt(v) || 0), 'number')}
                </div>
                <div className="mt-4 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                  <p className="text-sm text-green-300 font-semibold mb-2">✨ Calcul automatique :</p>
                  <div className="space-y-1 text-xs text-green-200">
                    <p>• Prix de base : {formule.pricing.days}j × 400€/j = <span className="font-bold">{formule.pricing.days * 400}€</span></p>
                    <p>• Réduction : -{formule.pricing.discount}% = -{Math.round(formule.pricing.days * 400 * formule.pricing.discount / 100)}€</p>
                    <p className="text-base font-bold text-green-100 mt-2 pt-2 border-t border-green-700">
                      → Prix final : {formule.pricing.finalPrice}€
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      ))}

      {/* BRIEF Section */}
      {content.brief && renderSection('brief', '💡 Brief Créatif', (
        <>
          {renderInput('Titre', content.brief?.title, (v) => updateField('brief', 'title', v))}
          {renderInput('Description', content.brief?.description, (v) => updateField('brief', 'description', v), 'textarea')}

          <div className="mt-6 space-y-6">
            <h3 className="text-lg font-bold text-primary-500">Sections du Brief</h3>
            <p className="text-sm text-secondary-300 -mt-4">💡 Astuce : Laissez le contenu vide pour masquer une section</p>

            {Object.entries(content.brief?.sections || {}).map(([key, section]) => (
              <div key={key} className="p-4 bg-secondary-800 rounded-lg border border-secondary-700">
                <h4 className="text-white font-semibold mb-3">{section.title}</h4>
                {renderInput('Titre de la section', section.title, (v) => updateBriefSection(key, 'title', v))}
                {renderInput('Contenu (Markdown supporté)', section.content, (v) => updateBriefSection(key, 'content', v), 'textarea')}
              </div>
            ))}
          </div>
        </>
      ))}

      {/* CONTACT Section */}
      {renderSection('contact', '📧 Section Contact', (
        <>
          {renderInput('Titre', content.contact?.title, (v) => updateField('contact', 'title', v))}
          {renderInput('Description', content.contact?.description, (v) => updateField('contact', 'description', v), 'textarea')}

          <div className="mt-6 p-4 bg-secondary-800 rounded-lg">
            <h3 className="text-white font-semibold mb-2">Informations de Contact</h3>
            {renderInput('Email', content.contact?.cta?.email, (v) => updateNestedField('contact', 'cta', 'email', v), 'email')}
            {renderInput('Téléphone', content.contact?.cta?.phone, (v) => updateNestedField('contact', 'cta', 'phone', v), 'tel')}
            {renderInput('Lien Calendly', content.contact?.cta?.calendly, (v) => updateNestedField('contact', 'cta', 'calendly', v), 'url')}
          </div>
        </>
      ))}

      {/* Help Section */}
      <Card padding="lg" className="mt-8 bg-primary-900/20 border-2 border-primary-500">
        <h3 className="text-lg font-bold text-white mb-3">📝 Astuces d'édition</h3>
        <ul className="space-y-2 text-secondary-200 text-sm">
          <li>• <strong className="text-white">Sections Brief :</strong> Laissez le contenu vide pour masquer automatiquement une section</li>
          <li>• <strong className="text-white">Markdown :</strong> Utilisez **gras** pour le texte en gras, - pour les listes</li>
          <li>• <strong className="text-white">Sauvegarde :</strong> Cliquez sur "Télécharger content.js" puis remplacez le fichier dans votre projet</li>
          <li>• <strong className="text-white">Étapes & Planning :</strong> Pour éditer ces sections complexes, modifiez directement les fichiers correspondants</li>
        </ul>
      </Card>
    </div>
  );
}
